package com.trevorwiebe.timesheet.invoicing.domain

import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.displayName
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceLine
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadRateInfo
import com.trevorwiebe.timesheet.loads.domain.model.LoadStop
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.domain.model.cityState
import kotlinx.datetime.LocalDate
import kotlin.math.abs
import kotlin.math.roundToLong

/** Loads already on an invoice (draft or sent) are never offered again. */
fun invoicedLoadIds(invoices: List<Invoice>): Set<String> =
    invoices.flatMapTo(mutableSetOf()) { it.loadIds }

/**
 * Delivered loads for [customerId] not yet on any invoice, oldest first. A null [start] means
 * "every outstanding load"; loads scheduled after [today] haven't been delivered and are left out.
 */
fun invoiceableLoads(
    loads: List<Load>,
    invoicedIds: Set<String>,
    customerId: String,
    start: LocalDate?,
    end: LocalDate,
    today: LocalDate,
): List<Load> = loads
    .filter { load ->
        val date = load.scheduledDate ?: return@filter false
        load.customerId == customerId &&
            load.id !in invoicedIds &&
            date <= today &&
            date <= end &&
            (start == null || date >= start)
    }
    .sortedWith(compareBy({ it.scheduledDate }, { it.loadNumber }))

/** A load with no rate, or a rate whose amounts were never filled in, can't be billed yet. */
val Load.isBillable: Boolean get() = (rate?.total ?: 0) > 0

fun buildDraftInvoice(
    customer: Customer,
    loads: List<Load>,
    locations: List<Location>,
    invoiceDate: LocalDate,
): Invoice = Invoice(
    id = "",
    number = 0,
    customerId = customer.id,
    customerName = customer.companyName.ifBlank { customer.contactName },
    paymentTerms = customer.paymentTerms,
    invoiceDate = invoiceDate,
    lines = loads.map { it.toInvoiceLine(locations) },
)

fun Load.toInvoiceLine(locations: List<Location>): InvoiceLine {
    val rate = rate
    fun names(stops: List<LoadStop>) = stops.joinToString(" / ") { stop ->
        locations.firstOrNull { it.id == stop.locationId }
            ?.let { loc -> listOf(loc.name, loc.cityState).filter { it.isNotBlank() }.joinToString(", ") }
            ?: "—"
    }
    return InvoiceLine(
        loadId = id,
        loadNumber = loadNumber,
        po = po,
        date = scheduledDate ?: originalDate,
        pickup = names(pickups),
        drop = names(drops),
        description = rate?.description() ?: "",
        base = rate?.baseAmount ?: 0,
        fuelSurcharge = rate?.fuelSurchargeAmount ?: 0,
        detention = rate?.detention ?: 0,
        stopOff = rate?.stopOffCharge ?: 0,
        other = rate?.otherCharge ?: 0,
        fuelSurchargePercent = rate?.fuelSurchargePercent ?: 0.0,
    )
}

/**
 * [invoice]'s lines rebuilt from the current state of their loads. A line whose load is gone, or
 * whose locations haven't loaded yet, is kept as it was.
 */
fun refreshedInvoiceLines(invoice: Invoice, loads: List<Load>, locations: List<Location>): List<InvoiceLine> {
    if (locations.isEmpty()) return invoice.lines
    val byId = loads.associateBy { it.id }
    return invoice.lines.map { line -> byId[line.loadId]?.toInvoiceLine(locations) ?: line }
}

private fun LoadRateInfo.description(): String = when (rateType) {
    LoadRateType.FLAT_RATE -> "${rateCategory.displayName} — flat rate"
    LoadRateType.PER_UNIT -> "${rateCategory.displayName} — ${unitDetail()}"
}

/** "25.00 tons @ $34.00" for a per-unit rate; blank for a flat rate. */
fun LoadRateInfo.unitDetail(): String = when (rateType) {
    LoadRateType.FLAT_RATE -> ""
    LoadRateType.PER_UNIT ->
        "${unitQuantity.twoDecimals()} ${unit?.displayName ?: "units"} @ $${perUnit.formatCentsAsDollars()}"
}

// Built from scaled longs because Double.toString renders differently on JVM and JS.
private fun Double.twoDecimals(): String {
    val scaled = (this * 100).roundToLong()
    val sign = if (scaled < 0) "-" else ""
    return "$sign${abs(scaled) / 100}.${(abs(scaled) % 100).toString().padStart(2, '0')}"
}
